import React, { useMemo } from 'react';
import { FieldGroup, Switch, withDataId, Icon, colors } from '@dexma/ui-components';
import {
    StyledFakeSelector,
    StyledPeriodTypeFieldGroup,
    StyledRadioButtonWrapper,
    StyledSelectablePeriodTemplate,
    StyledSwitchGroup,
} from 'adapter/in/common/components/PeriodSelector/style';

import { areRollingStatesEqual } from 'adapter/in/common/components/PeriodSelector/periodSelectorUtils';
import { PeriodSelectorTexts, PeriodType, SelectablePeriodTemplateState } from 'adapter/in/common/components/PeriodSelector';
import gtm from 'adapter/in/common/config/gtm';
import { PeriodFormState } from 'adapter/in/common/form/types';

type PeriodTypeFieldGroupProps = {
    title: string;
    texts: PeriodSelectorTexts;
    handleChange: (id: PeriodType) => void;
    selectedPeriodType: PeriodType;
};
export const PeriodTypeFieldGroup = (props: PeriodTypeFieldGroupProps) => {
    const { title, texts, handleChange, selectedPeriodType } = props;

    const correctText = useMemo(() => (selectedPeriodType === PeriodType.FIXED ? texts.periodTypeFixedText : texts.periodTypeRollingText), [
        selectedPeriodType,
    ]);

    return (
        <StyledPeriodTypeFieldGroup>
            <span>{title}</span>
            <FieldGroup
                onChange={(e: { id: PeriodType }) => {
                    handleChange(e.id);
                }}
                selectedValues={selectedPeriodType}
                dataId={gtm.periodSelector.rollingAndFixed}
                values={[
                    { value: PeriodType.ROLLING, label: texts.periodTypeRollingLabel, id: PeriodType.ROLLING },
                    { value: PeriodType.FIXED, label: texts.periodTypeFixedLabel, id: PeriodType.FIXED },
                ]}
            />
            <span className='period-type-description'>{correctText}</span>
        </StyledPeriodTypeFieldGroup>
    );
};

export type SelectablePeriodTemplatesProps = {
    periodValues?: SelectablePeriodTemplateState[];
    handleChange: (state: PeriodFormState) => void;
    formState: PeriodFormState;
    primaryColor: string;
    areDisabled: boolean;
};
export const SelectablePeriodTemplates = (props: SelectablePeriodTemplatesProps) => {
    const { periodValues, handleChange, formState, primaryColor, areDisabled: isDisabled } = props;

    const handleRadioChange = (radioState: PeriodFormState) => {
        handleChange(radioState);
    };

    return (
        <StyledSelectablePeriodTemplate>
            {periodValues?.map((periodTemplate: SelectablePeriodTemplateState) => (
                <PeriodRadioButton
                    color={primaryColor}
                    isDisabled={isDisabled}
                    key={periodTemplate.id}
                    label={periodTemplate.label}
                    radioButtonState={periodTemplate.state}
                    isChecked={areRollingStatesEqual(periodTemplate.state, formState)}
                    handleRadioChange={handleRadioChange}
                    dataId={`${gtm.periodSelector.template}.${periodTemplate.id}`}
                />
            ))}
        </StyledSelectablePeriodTemplate>
    );
};

type PeriodRadioButtonProps = {
    label: string;
    isChecked: boolean;
    handleRadioChange: (state: PeriodFormState) => void;
    radioButtonState: PeriodFormState;
    color: string;
    isDisabled: boolean;
    dataId: string;
};
const PeriodRadioButtonNoId = (props: PeriodRadioButtonProps) => {
    const { label, isChecked, handleRadioChange, radioButtonState, color, isDisabled, dataId } = props;

    return (
        <StyledRadioButtonWrapper color={color} checked={isChecked} isDisabled={isDisabled} tabIndex={0}>
            <input
                onClick={
                    !isDisabled
                        ? () => {
                              handleRadioChange(radioButtonState);
                          }
                        : undefined
                }
                type='radio'
                name='periodValue'
                checked={isChecked}
                disabled={isDisabled}
                readOnly
                data-id={dataId}
            />
            <span>{label}</span>
        </StyledRadioButtonWrapper>
    );
};

export const PeriodRadioButton = withDataId(PeriodRadioButtonNoId);

type SwitchGroupProps = {
    title: string;
    isChecked: boolean;
    onChange: () => void;
    isDisabled?: boolean;
    id?: string;
};
export const SwitchGroup = (props: SwitchGroupProps) => {
    const { title, isChecked, onChange, isDisabled, id } = props;

    return (
        <StyledSwitchGroup>
            <Switch
                dataId={`${gtm.periodSelector.switch}.${id}`}
                disabled={isDisabled}
                size='small'
                checked={isChecked}
                onChange={() => onChange()}
            />
            <span>{title}</span>
        </StyledSwitchGroup>
    );
};

type FakeSelectorProps = {
    dataId: string;
    color: string;
    disabled: boolean;
    label: string;
    isUIOpen: boolean;
};
const FakeSelectorNoId = (props: FakeSelectorProps) => (
    <StyledFakeSelector data-id={props.dataId} color={props.color} tabIndex={0} $disabled={props.disabled}>
        <span>{props.label}</span>
        <Icon name={props.isUIOpen ? 'chevron_up' : 'chevron_down'} size='small' color={colors.gray} />
    </StyledFakeSelector>
);

export const FakeSelector = withDataId(FakeSelectorNoId);
